import React, { useEffect, useState } from "react";
import styles from "./NFTCard.module.scss";
import { NFTItemClickFunc, ClickType, NFTCardItem } from "@/interfaces/nftTypes";
import cn from "classnames";
import Image from "next/image";

import RarityStars from "./RarityStars";
import { LikeFilledIcon, UnLikeFilledIcon } from "../RFIcon";
import Skeleton from "../skeleton/Skeleton";
import clsx from "clsx";

interface NFTCardProps extends NFTCardItem {
	onLikedClick?: Function;
	onCardClick?: NFTItemClickFunc;
	className?: string;
	children?: React.ReactNode;
	loadLarge?: boolean;
}

export default function NFTCard({
	id,
	loadUrl,
	name,
	likedBy,
	starRank = undefined,
	owner = "",
	withLikedIcon = false,
	isFavorite,
	onLikedClick,
	onCardClick,
	className,
	children,
	loadLarge = false,
	isMystery = false,
	isFrozen = false,
}: NFTCardProps) {
	const [loading, setLoading] = useState(true);
	const [favor, setFavor] = useState(isFavorite);

	useEffect(() => {
		setFavor(isFavorite);
	}, [isFavorite]);

	useEffect(() => {
		setLoading(true);
	}, [loadUrl]);

	const handleLoadingComplete = () => {
		setLoading(false);
	};

	const withRarityStar = () => {
		return typeof starRank === "number";
	};

	const renderName = (name: string) => {
		if (loading) {
			return (
				<div>
					<Skeleton className="h-2 w-10 rounded-3xl" />
					<Skeleton className="h-2 w-10 rounded-3xl mt-2" />
				</div>
			);
		}

		return (
			<div className={styles.item}>
				<div className="text-xxs inter-text">Name</div>
				<div className={styles.value}>{name}</div>
			</div>
		);
	};

	const renderOwner = (owner: string | undefined) => {
		if (!owner) {
			return null;
		}

		if (loading) {
			return (
				<div>
					<Skeleton className="h-2 w-10 rounded-3xl" />
					<Skeleton className="h-2 w-10 rounded-3xl mt-2" />
				</div>
			);
		}

		return (
			<div className={cn(styles.item, styles.itemCenter)}>
				<div className="text-xxs inter-text">Owner</div>
				<div className={styles.value}>{owner}</div>
			</div>
		);
	};

	const renderLikeBy = (likedBy: string | number) => {
		if (loading) {
			return (
				<div>
					<Skeleton className="h-2 w-10 rounded-3xl" />
					<Skeleton className="h-2 w-10 rounded-3xl mt-2" />
				</div>
			);
		}

		return (
			<div className={styles.item}>
				<div className="text-xxs inter-text">Liked by</div>
				<div className={styles.value}>{likedBy}</div>
			</div>
		);
	};

	const renderLikedIcon = () => {
		if (!withLikedIcon) {
			return null;
		}

		if (favor) {
			return (
				<div className={styles.likedIconWrap}>
					<div onClick={(e: any) => handleLikedIconClick(false, e)}>
						<LikeFilledIcon className="text-pink_FF38A3 w-3.5 h-3.5" />
					</div>
				</div>
			);
		}

		return (
			<div className={styles.likedIconWrap}>
				<div className="inline-block" onClick={(e: any) => handleLikedIconClick(true, e)}>
					<UnLikeFilledIcon className="text-pink_E1D2D2 w-3.5 h-3.5" />
				</div>
			</div>
		);
	};

	const handleLikedIconClick = (liked: boolean, evt: MouseEvent) => {
		evt.stopPropagation();
		// setFavor(liked);
		onCardClick && onCardClick(id ? id : name, ClickType.NFT_CARD_LIKED_ICON, liked);
	};

	const handleClick = () => {
		let clickType = ClickType.NFT_CARD_ITEM;
		if (isMystery) {
			clickType = ClickType.NTF_CARD_MYSTERY;
		}
		onCardClick && onCardClick(id ? id : name, clickType);
	};

	const renderStar = () => {
		if (loading) {
			return <Skeleton className="h-2 w-20 rounded-3xl" />;
		}
		return <RarityStars starRank={starRank as number} />;
	};

	return (
		<article
			className={cn("bg-f6f7f9 dark:bg-151515", styles.card, className, isFrozen ? styles.isFrozen : "")}
			onClick={() => handleClick()}
		>
			<div className={clsx(styles.cover, "h-0 pb-[100%]")}>
				{loading && <Skeleton className="h-full w-full rounded-t-xl absolute left-0 top-0 z-[1] " />}
				{loadUrl && (
					<div className="absolute left-0 top-0 h-full w-full overflow-hidden">
						<Image
							src={loadUrl}
							onLoad={handleLoadingComplete}
							className="h-full w-full rounded-t-xl object-cover"
							alt="nfts"
							width={loadLarge ? 400 : 200}
							height={loadLarge ? 400 : 200}
						/>
					</div>
				)}

				{renderLikedIcon()}
			</div>
			<div className={cn(styles.content, withRarityStar() ? styles.withRarityStar : "")}>
				{withRarityStar() ? <div className={styles.starsWrap}>{renderStar()}</div> : null}
				{isMystery ? (
					<div className={styles.mystery}>
						{name.split("/").map((item) => (
							<span key={item}>{item}</span>
						))}
					</div>
				) : (
					<div className={styles.description}>
						{renderName(name)}
						{renderOwner(owner)}
						{renderLikeBy(likedBy)}
					</div>
				)}

				<div className={styles.actions}>{children}</div>
			</div>
		</article>
	);
}
